import React from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Info } from 'lucide-react';
import { getProfile } from '../utils/storage';

const CONFIDENCE_TEXT = {
    high: 'Supported by multiple human trials or strong epidemiological consensus.',
    moderate: 'Supported by limited human data or high-quality animal models.',
    low: 'Based on preliminary animal studies, in-vitro models, or mechanistic pathways.',
    very_low: 'Theoretical concern based on structural similarities or very limited studies.'
};

const EVIDENCE_NOTES = {
    direct: 'This is based on studies of this specific ingredient.',
    food_data: 'This is based on nutritional or processing characteristics of the food.',
    proxy: 'This factor was inferred based on the product category or related ingredients.'
};

const AXIS_LABELS = {
    metabolic: { label: 'Metabolic', color: 'var(--metabolic-start)' },
    stress: { label: 'Stress', color: 'var(--stress-start)' },
    reproductive: { label: 'Reproductive', color: 'var(--repro-start)' },
    thyroid: { label: 'Thyroid', color: 'var(--thyroid-start, #06b6d4)' },
};



export default function FactorDetailModal({ signal, signalType, productCategory, onClose }) {
    if (!signal) return null;

    const profile = getProfile() || {};
    const isFood = signalType === 'food';
    const title = isFood ? signal.reason : signal.shortReason;
    const description = isFood ? signal.mechanism : signal.detailReason;
    const evidenceLabel = isFood ? 'food data' : (signal.evidenceType || 'unknown');
    const evidenceClass = isFood ? 'food-tag' : (signal.evidenceType || 'proxy');
    const confidenceLevel = signal.confidence || (isFood ? 'moderate' : 'low');

    // Extract affected axes from impacts
    const impacts = signal.impacts || {};
    const affectedAxes = Object.entries(impacts)
        .filter(([, val]) => val !== 0)
        .map(([axis, delta]) => ({
            axis,
            ...(AXIS_LABELS[axis] || { label: axis, color: '#94a3b8' }),
            severity: Math.abs(delta),
        }));

    const contextNotes = [];
    if (profile.pcos && affectedAxes.some(a => a.axis === 'metabolic')) contextNotes.push('Because you have PCOS, metabolic signals are weighted more heavily for you.');
    else if (profile.pcos && affectedAxes.some(a => a.axis === 'reproductive')) contextNotes.push('Because you have PCOS, reproductive signals are weighted more heavily for you.');
    if (profile.acneProne && affectedAxes.some(a => a.axis === 'skin')) contextNotes.push('Because you selected acne-prone/sensitive skin, skin & microbiome signals are weighted more heavily for you.');
    if (profile.ttc && affectedAxes.some(a => a.axis === 'reproductive')) contextNotes.push('Because you are trying to conceive, reproductive signals are weighted more heavily for you.');
    if (profile.thyroidCondition && affectedAxes.some(a => a.axis === 'thyroid')) contextNotes.push('Because you have a thyroid condition, thyroid signals are weighted more heavily for you.');
    if (profile.pregnant && affectedAxes.length > 0) contextNotes.push('Because you are pregnant, endocrine signals in general are weighted more heavily for you.');
    const userContextString = contextNotes[0];

    return createPortal(
        <div className="factor-modal-overlay" onClick={onClose}>
            <div className="factor-modal" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="factor-modal-header">
                    <div>
                        <h3 className="factor-modal-title">{title}</h3>
                        <span className={`evidence-badge ${evidenceClass}`}>{evidenceLabel}</span>
                    </div>
                    <button className="factor-modal-close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                {/* Mechanism / What This Means */}
                <div className="factor-modal-section">
                    <div className="factor-modal-section-title">What this means</div>
                    <p className="factor-modal-desc">{description}</p>
                </div>

                {/* Affected Hormone Axes */}
                {affectedAxes.length > 0 && (
                    <div className="factor-modal-section">
                        <div className="factor-modal-section-title">Affected hormone axes</div>
                        <div className="axis-pills">
                            {affectedAxes.map(({ axis, label, color, severity }) => (
                                <div key={axis} className="axis-pill" style={{ '--pill-color': color }}>
                                    <span className="axis-pill-label">{label}</span>
                                    <span className="axis-pill-severity">
                                        {'●'.repeat(Math.min(severity, 5))}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Confidence Level */}
                <div className="factor-modal-section">
                    <div className="factor-modal-section-title">Confidence Level</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span className={`confidence-text conf-${confidenceLevel}`} style={{ textTransform: 'capitalize', fontWeight: 600 }}>
                            {confidenceLevel} Confidence
                        </span>
                    </div>
                    <p className="factor-modal-desc" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {CONFIDENCE_TEXT[confidenceLevel] || CONFIDENCE_TEXT.low}
                    </p>
                </div>

                {/* Evidence Note */}
                <div className="factor-modal-section">
                    <div className="factor-modal-section-title">Evidence Context</div>
                    <p className="factor-modal-desc" style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                        {isFood ? EVIDENCE_NOTES.food_data : (EVIDENCE_NOTES[signal.evidenceType] || EVIDENCE_NOTES.proxy)}
                        {' Concentration, combination, and individual genetics affect actual impact.'}
                    </p>
                    {signal.exposureNote && !isFood && (
                        <p className="factor-modal-desc" style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>
                            <strong>Common in:</strong> {signal.exposureNote}
                        </p>
                    )}
                </div>

                {/* User Context */}
                {userContextString && (
                    <div className="factor-modal-section" style={{ background: 'var(--bg-glass-hover)', padding: '12px', borderRadius: 8 }}>
                        <div className="factor-modal-section-title" style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text-primary)' }}>
                            <Info size={14} />
                            Your Profile Context
                        </div>
                        <p className="factor-modal-desc" style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
                            {userContextString}
                        </p>
                    </div>
                )}

                {/* Food-specific context */}
                {isFood && (
                    <div className="factor-modal-section">
                        <div className="factor-modal-section-title">Why it matters for hormones</div>
                        <p className="factor-modal-desc">
                            {productCategory === 'food' 
                                ? 'Dietary factors influence hormone levels through digestion, absorption, and metabolic processing. The impacts shown reflect research-based associations between food properties and hormone axis disruption.'
                                : 'This factor relates to dietary properties of the product.'
                            }
                        </p>
                    </div>
                )}

                {/* Divider */}
                <div className="factor-modal-divider" />

                {/* Better Alternatives (placeholder) */}
                <div className="factor-modal-section">
                    <div className="factor-modal-section-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Sparkles size={14} />
                        Better Alternatives
                    </div>
                    <div className="alt-coming-soon">
                        Personalized alternatives coming soon
                    </div>
                </div>

            </div>
        </div>,
        document.body
    );
}
